import React, {forwardRef, useImperativeHandle, useRef, useState} from "react";
import {IconButton, Modal, TextField, Typography} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import {SetupMfa} from "./SetupMfa";
import VerifyMFAModal from "./VerifyMFAModal";
import {apiConfig} from "../../api/ApiConfig";
import {useNavigate} from "react-router-dom";
import {RedText} from "../../style/StyledElement";
import {encrypt} from "../../utils/AppUtils";

const {validateAndFetchDeviceQR, getUserByUserId, getPublicKey, confirmSignUp} = apiConfig();

const ForgotPasswordModal = forwardRef(function ForgotPasswordModal({props}, ref) {
        const [isModalOpen, setIsModalOpen] = useState(false);
        const [userDetail, setUserDetail] = useState({
            userName: null,
            password: null,
            isValidUser: false,
            isValidPassword: false
        });
        const [mfa, setMfa] = useState({mfaEnabled: false, qrCodeUrl: null});
        const mfaModalRef = useRef()
        const [error, setError] = useState(null);
        const navigate = useNavigate();

        const handleClickVerify = () => {
            mfaModalRef.current.open();
        }

        useImperativeHandle(ref, () => {
            return {
                open() {
                    setIsModalOpen(true)
                },
                close() {
                    setIsModalOpen(false)
                }
            }
        });

        const doNavigateTo = (navigateTo) => {
            if (window.location.pathname === navigateTo) {
                window.location.reload();
            } else {
                navigate(navigateTo);
            }
            mfaModalRef.current.close();
        }

        const verifyOtp = async (otp) => {
            const response = await confirmSignUp(otp, userDetail.userName, true)
            console.log(`Verify OTP response - ${response}`)
            return {success: response, nextStep: () => doNavigateTo('/login')}
        }

        const onUserDetailsInputChange = (type, value) => {
            if (type === 'UserId') {
                setUserDetail((prevUserDetail) => {
                    return (
                        {
                            ...prevUserDetail,
                            userName: value,
                        }
                    );
                });
            }
            if (type === 'Password') {
                setUserDetail((prevUserDetail) => {
                    return (
                        {
                            ...prevUserDetail,
                            password: value,
                        }
                    );
                });
            }
        }

        const handleOnBlurUserIdInput = async () => {
            try {
                const response = await getUserByUserId(userDetail.userName);
                if (response) {
                    setUserDetail((prevUserDetail) => {
                        return (
                            {
                                ...prevUserDetail,
                                isValidUser: true,
                            }
                        );
                    });
                    setError(null);
                } else {
                    setError('Invalid User');
                }
            } catch (error) {
                setError('Failed to fetch user');
            }
        }

        const handleOnBlurPasswordInput = async () => {
            try {
                if (!userDetail.password) {
                    setError('Password is mandatory')
                    return;
                }
                const publicKey = await getPublicKey(userDetail.userName);
                const encPass = btoa(await encrypt(userDetail.password, publicKey))
                const user = {
                    'userId': userDetail.userName,
                    'password': encPass
                };
                const response = await validateAndFetchDeviceQR(JSON.stringify(user));
                console.log(response.mfaEnabled);
                console.log(response.qrCodeUrl);
                console.log(response.active);
                if (!response.errors) {
                    setUserDetail((prevUserDetail) => {
                        return (
                            {
                                ...prevUserDetail,
                                isValidPassword: true,
                            }
                        );
                    });
                    setMfa({
                        mfaEnabled: response.mfaEnabled,
                        qrCodeUrl: response.qrCodeUrl
                    });
                    setError(null);
                } else {
                    setError(response.errors);
                }
            } catch (error) {
                setError(error.message ? error.message : 'Failed to validate user Password.');
            }
        }

        const handleOnModalClose = () => {
            setIsModalOpen(false);
            setUserDetail({
                userName: null,
                password: null,
                isValidUser: false,
                isValidPassword: false
            });
            setMfa({
                mfaEnabled: false,
                qrCodeUrl: null
            });
            setError(null);
        }

        const textfieldStyle = {
            backgroundColor: '#f9f9f9',
            borderRadius: '8px',
            marginBottom: '15px',
            input: {
                color: '#000',
            },
            '& label': {
                color: '#888',
            },
            '& label.Mui-focused': {
                fontSize: '20px',
            },
            '& .MuiOutlinedInput-root': {
                '& fieldset': {
                    borderColor: '#ccc',
                },
                '&:hover fieldset': {
                    borderColor: '#999',
                },
                '&.Mui-focused fieldset': {
                    fontSize: '20px',
                },
            },
        }

        return (

            <Modal
                open={isModalOpen}
                onClose={handleOnModalClose}
                aria-labelledby="forgotPassword-modal-title"
                aria-describedby="forgotPassword-modal-description"
            >
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: {
                            xs: '90vw',  // phones
                            sm: '80vw',  // tablets
                            md: 650      // desktops
                        },
                        maxHeight: '90vh',
                        overflowY: 'auto',
                        bgcolor: "rgba(224, 224, 224, 1)",
                        boxShadow: 24,
                        borderRadius: 2,
                        p: { xs: 2, sm: 3, md: 4 },  // responsive padding
                    }}
                >
                    {/* Modal Header */}
                    <Box sx={{width:"100%", display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4}}>
                        <Typography id="verifymfa-modal-title" variant="h6" component="h2" sx={{ fontWeight: "bold" }}>
                            Set up Google Authenticator (Two-Factor Authentication)
                        </Typography>
                        <IconButton onClick={handleOnModalClose}>
                            <CloseIcon/>
                        </IconButton>
                    </Box>
                    <main style={{
                        gap: "2rem",
                        height: "65vh",
                        marginBlock: "8px",
                    }}>
                        <RedText> {error} </RedText>
                        <TextField
                            label="User Id"
                            type="text"
                            value={userDetail.userName}
                            onChange={(event) => onUserDetailsInputChange('UserId', event.target.value)}
                            fullWidth
                            disabled={userDetail.isValidUser}
                            onBlur={handleOnBlurUserIdInput}
                            sx={textfieldStyle}
                        />
                        {userDetail.isValidUser && <TextField
                            label="Verify Password"
                            type="password"
                            value={userDetail.password}
                            onChange={(event) => onUserDetailsInputChange('Password', event.target.value)}
                            fullWidth
                            disabled={userDetail.isValidPassword}
                            onBlur={handleOnBlurPasswordInput}
                            sx={textfieldStyle}
                        />}
                        {userDetail.isValidPassword && mfa.qrCodeUrl &&
                            <SetupMfa mfa={mfa} handleClick={handleClickVerify}/>
                        }
                    </main>
                    <VerifyMFAModal handleVerifyOtp={verifyOtp} ref={mfaModalRef}/>
                </Box>
            </Modal>
        );
    }
);

export default ForgotPasswordModal;